import type { Permission } from '@fieldvision/contracts';
import { can, canAny, useAccessScope } from '@fieldvision/web-core';
import {
  ChevronRight,
  Database,
  MapPin,
  Package,
  Store,
  Tags,
  Target,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { Panel, PanelHeader } from '../../components/ui/index.js';

/**
 * Master Data CRUD, as a dashboard can honestly represent it.
 *
 * CRUD DOES NOT HAPPEN HERE, and that is the design rather than a shortcut.
 * Creating or editing a master row needs its own screen: field-level validation,
 * an audit entry, and — per design-guidelines §11.5 — a "Nonaktifkan"
 * confirmation that states what survives deactivation. Inlining a form on a
 * dashboard would give up all three to save one click.
 *
 * What belongs here is the route to those screens, and the gate is the point.
 *
 * GATED ON WRITE PERMISSIONS, NOT READ. Every SUPERVISOR holds `outlet:read`
 * and `sku:read` — the field app needs them — so gating on read
 * would put a "manage master data" panel in front of someone who can only look
 * at it. Gating on write means REGIONAL_MANAGER sees exactly Outlet and Distributor
 * (they hold `outlet:write` alone), ADMIN sees everything, and SUPERVISOR sees
 * no panel at all. That difference is a verification assertion, not a detail.
 *
 * Icons are lifted verbatim from `nav.ts`: same concept, same glyph (§8.5).
 */
interface MasterLink {
  to: string;
  label: string;
  description: string;
  icon: LucideIcon;
  need: Permission;
}

const LINKS: MasterLink[] = [
  { to: '/master/outlets', label: 'Outlet', description: 'Master outlet dan toleransi GPS', icon: Store, need: 'outlet:write' },
  { to: '/master/regions', label: 'Distributor', description: 'Sumbu cakupan setiap pengguna', icon: MapPin, need: 'outlet:write' },
  { to: '/master/skus', label: 'SKU', description: 'Produk dan rencana rak', icon: Package, need: 'sku:write' },
  { to: '/master/competitors', label: 'Kompetitor', description: 'Master kompetitor', icon: Tags, need: 'sku:write' },
  { to: '/master/visit-targets', label: 'Target Kunjungan', description: 'Rencana per petugas per bulan', icon: Target, need: 'target:write' },
  { to: '/users', label: 'Pengguna', description: 'Akun dan peran', icon: Users, need: 'user:write' },
];

export function MasterDataPanel() {
  const scope = useAccessScope();
  const visible = LINKS.filter((link) => can(scope.data, link.need));

  // Rendered only for someone who maintains master data. Not a "you do not have
  // access" box — an empty panel telling a supervisor what they cannot do is
  // worse than no panel.
  if (!canAny(scope.data, LINKS.map((link) => link.need))) return null;

  return (
    <Panel className="flex h-full flex-col">
      <div className="flex items-start gap-2.5">
        <Database size={18} className="mt-1 shrink-0 text-steel" aria-hidden />
        <div className="min-w-0 flex-1">
          <PanelHeader title="Data Induk" description="Pemeliharaan master data." />
        </div>
      </div>

      <ul className="flex flex-col gap-1">
        {visible.map((link) => (
          <li key={link.to}>
            <Link
              to={link.to}
              className="focus-ring flex items-center gap-3 rounded-xl px-2 py-2 transition-colors duration-150 hover:bg-tint"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-panel text-steel">
                <link.icon size={18} aria-hidden />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-ink">{link.label}</span>
                <span className="block truncate text-sm text-muted">{link.description}</span>
              </span>
              <ChevronRight size={16} className="shrink-0 text-muted" aria-hidden />
            </Link>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
